import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as KeyIcon } from '../../assets/icons/key.svg';
import { ReactComponent as UserEditIcon } from '../../assets/icons/user-edit.svg';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import SearchBar from '../SearchBar/SearchBar';
import TableauDeBord from '../TableauDeBord/TableauDeBord';
import Button from '../Button/Button';
import CreationComptesModal from '../CreationComptesModal/CreationComptesModal';
import EditUserModal from '../EditUserModal/EditUserModal';
import PassationModal from '../PassationModal/PassationModal';
import {
  convertApiRoleToUiRole,
  convertUiRoleToApiRole,
  isClientRole,
  isPrestataireRole,
  isPrestataireLibreRole,
  isAdminRole
} from '../../utils/roleUtils';
import './GestionComptes.css';
import './menu-actions.css'; // Ajout du style spécifique pour les menus


// Fonction utilitaire pour filtrer les doublons d'utilisateurs
// Garde uniquement le rôle le plus élevé pour chaque id_user
// Priorité : Acheteur signataire admin > Acheteur signataire > Acheteur
// IMPORTANT: Pour les prestataires avec lots, on garde toutes les lignes (une par lot/département)
const filterDuplicateUsers = (users: any[]): any[] => {
  const rolePriority: { [key: string]: number } = {
    'Acheteur signataire admin': 3,
    'Acheteur signataire': 2,
    'Acheteur': 1,
    'Prestataire signataire admin': 3,
    'Prestataire signataire': 2,
    'Prestataire': 1,
  };

  // Grouper les utilisateurs par clé composite (idUser + lot technique + département + contrat)
  // Cela permet de garder plusieurs lignes pour le même utilisateur s'il a des lots différents
  const usersByKey = new Map<string, any[]>();

  users.forEach(user => {
    // Pour les prestataires avec des lots, créer une clé composite unique
    // qui inclut l'id utilisateur + les infos de lot
    let key: string;
    if (user.lotTechniqueId !== undefined && user.lotDepartementId !== undefined) {
      // Clé composite pour les prestataires avec lots : garder chaque combinaison unique
      key = `${user.idUser || user.id}-${user.lotTechniqueId}-${user.lotDepartementId}-${user.contratNumero || ''}`;
    } else if (user.idUser) {
      // Pour les utilisateurs sans lots (acheteurs), grouper par idUser
      key = `user-${user.idUser}`;
    } else {
      // Si pas d'idUser, utiliser l'id
      key = `id-${user.id}`;
    }

    const existing = usersByKey.get(key) || [];
    existing.push(user);
    usersByKey.set(key, existing);
  });

  // Pour chaque groupe, garder uniquement celui avec le rôle le plus élevé
  const filteredUsers: any[] = [];

  usersByKey.forEach((userGroup) => {
    if (userGroup.length === 1) {
      filteredUsers.push(userGroup[0]);
    } else {
      // Trier par priorité de rôle et garder le plus élevé
      const sorted = userGroup.sort((a, b) => {
        const priorityA = rolePriority[a.role] || 0;
        const priorityB = rolePriority[b.role] || 0;
        return priorityB - priorityA;
      });
      filteredUsers.push(sorted[0]);
    }
  });

  return filteredUsers;
};

interface Column {
  field: string;
  header: string;
  renderCell: (value: any, row: any) => React.ReactNode;
}

interface GestionComptesProps {
  userType?: 'acheteur' | 'prestataire';  // Type d'utilisateur pour filtrer les données
}

const GestionComptes: React.FC<GestionComptesProps> = ({ userType = 'acheteur' }) => {
  const { user } = useAuth();
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [validatorsCount, setValidatorsCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [tempSelectedRole, setTempSelectedRole] = useState<string | null>(null);
  const [userData, setUserData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // États pour les filtres (prestataires)
  const [lotTechniqueFilter, setLotTechniqueFilter] = useState<string>('');
  const [lotDepartementFilter, setLotDepartementFilter] = useState<string>('');
  const [contratFilter, setContratFilter] = useState<string>('');
  const [availableLotsTechniques, setAvailableLotsTechniques] = useState<any[]>([]);
  const [availableLotsDepartements, setAvailableLotsDepartements] = useState<any[]>([]);
  const [availableContrats, setAvailableContrats] = useState<string[]>([]);

  // État pour la modal de réattribution
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [selectedLotContrat, setSelectedLotContrat] = useState<any>(null);
  const [availableInterlocuteurs, setAvailableInterlocuteurs] = useState<any[]>([]);
  const [newInterlocuteurId, setNewInterlocuteurId] = useState<string>('');
  const [isReassigning, setIsReassigning] = useState(false);

  // États pour la modal de passation (limite 3 gestionnaires)
  const [isPassationModalOpen, setIsPassationModalOpen] = useState(false);
  const [gestionnairesActifs, setGestionnairesActifs] = useState<any[]>([]);
  const [pendingPromotion, setPendingPromotion] = useState<any>(null);

  // FRONT-16: États pour la modale d'attribution de membre à un lot (Prestataires Libres)
  // Logique inversée: On sélectionne un LOT dans la grille, puis on choisit un MEMBRE dans le modal
  const [isLotAssignModalOpen, setIsLotAssignModalOpen] = useState(false);
  const [selectedLotForMember, setSelectedLotForMember] = useState<any>(null); // Le lot sélectionné
  const [selectedMemberId, setSelectedMemberId] = useState<string>(''); // Le membre à assigner
  const [availableMembersForLot, setAvailableMembersForLot] = useState<any[]>([]); // Liste des membres disponibles
  const [isAssigningLot, setIsAssigningLot] = useState(false);
  const [entiteLotsTechniques, setEntiteLotsTechniques] = useState<any[]>([]);
  const [entiteLotsDepartements, setEntiteLotsDepartements] = useState<any[]>([]);

  // Utiliser isClientRole pour déterminer le type d'utilisateur
  const userRole = user?.role || '';
  console.log('GestionComptes - Current user role:', userRole);

  // Vérifier si l'utilisateur est un client ou un prestataire
  const isClient = isClientRole(userRole);
  const isPrestataireLibre = isPrestataireLibreRole(userRole);
  const isPrestataire = isPrestataireRole(userRole) && !isPrestataireLibre; // Exclure les prestataires libres
  const isAdmin = isAdminRole(userRole);

  console.log('Is client role:', isClient);
  console.log('Is prestataire role:', isPrestataire);
  console.log('Is prestataire libre role:', isPrestataireLibre);
  console.log('Is admin role:', isAdmin);

  // Définition dynamique des tabs selon le rôle
  const tabs = isClient
    ? ['Tous', 'Acheteur signataire admin', 'Acheteur signataire', 'Acheteur']
    : ['Tous', 'Prestataire signataire admin', 'Prestataire signataire', 'Interlocuteur'];

  // Charger les données des utilisateurs depuis l'API
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("Token d'authentification non trouvé");

        // Transformer les données des organismes/entités en une liste d'utilisateurs
        const users: any[] = [];

        if (isPrestataireLibre) {
          // ============================================
          // FRONT-16: Traitement pour les PRESTATAIRES LIBRES
          // Affiche les LOTS de l'entité avec le membre assigné à chaque lot
          // L'admin PL peut modifier le MEMBRE assigné à un lot via un dropdown
          // (Même logique que pour les AC)
          // ============================================
          console.log('GestionComptes - Mode Prestataire Libre');

          // Récupérer le profil prestataire libre
          const profileResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!profileResponse.ok) {
            throw new Error('Erreur lors de la récupération du profil');
          }

          const profileData = await profileResponse.json();
          if (profileData.status !== 'success' || !profileData.data) {
            throw new Error('Format de réponse API inattendu');
          }

          const profile = profileData.data;
          const entite = profile.entite;

          // FRONT-16: Récupérer les lots de l'entité PL
          const lotsDepartementsLibre = profile.prestataire_libre?.lots_departements || [];

          // Récupérer les membres de l'équipe via /profile/team
          const teamResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile/team`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          let membres: any[] = [];
          if (teamResponse.ok) {
            const teamData = await teamResponse.json();
            if (teamData.status === 'success' && teamData.data?.membres) {
              membres = teamData.data.membres;
            }
          }

          // Stocker les membres pour le dropdown dans la modale
          setAvailableMembersForLot(membres.map(m => ({
            id: m.id,
            nom: `${m.prenom} ${m.nom}`,
            email: m.email,
            role: m.role
          })));

          // FRONT-16: Créer un index des attributions par lot
          // Structure: { "lotTechniqueId_departementId": { membre, lotAssignmentId } }
          const assignmentsByLot: { [key: string]: any } = {};
          for (const membre of membres) {
            const lotsAttribues = membre.lots_attribues || [];
            for (const lotAttribue of lotsAttribues) {
              const key = `${lotAttribue.lot_technique_id}_${lotAttribue.departement_id}`;
              assignmentsByLot[key] = {
                membre: membre,
                lotAssignmentId: lotAttribue.id
              };
            }
          }

          // FRONT-16: Afficher CHAQUE LOT de l'entité avec le membre assigné
          for (const lotDept of lotsDepartementsLibre) {
            const key = `${lotDept.lot_technique_id}_${lotDept.departement_id}`;
            const assignment = assignmentsByLot[key];

            users.push({
              id: `lot_${lotDept.lot_technique_id}_${lotDept.departement_id}`, // ID unique pour la ligne
              // Info du lot (lecture seule)
              lotTechnique: {
                id: lotDept.lot_technique_id,
                nom: lotDept.lot_technique_nom || ''
              },
              lotDepartement: {
                id: lotDept.departement_id,
                nom: lotDept.departement_nom || ''
              },
              lotTechniqueId: lotDept.lot_technique_id,
              lotDepartementId: lotDept.departement_id,
              contratNumero: '', // PL n'a pas de contrats
              type: assignment ? 'Référencé' : 'Non attribué',
              // Info du membre assigné (ou vide)
              nom: assignment ? `${assignment.membre.prenom} ${assignment.membre.nom}` : '-',
              email: assignment ? assignment.membre.email : '',
              telephone: assignment ? (assignment.membre.telephone || '') : '',
              role: assignment ? (assignment.membre.role || 'Membre') : '-',
              dateMiseAJour: assignment
                ? new Date(assignment.membre.updated_at || assignment.membre.created_at).toLocaleDateString('fr-FR')
                : '-',
              // IDs pour la modification
              assignedMemberId: assignment ? assignment.membre.id : null,
              lotAssignmentId: assignment ? assignment.lotAssignmentId : null,
              entiteId: entite?.id,
              isPrestataireLibreLot: true // Flag pour identifier les lignes de lots PL
            });
          }

          // FRONT-16: Extraire les lots uniques pour les filtres
          const uniqueLotsTechniques = new Map();
          const uniqueLotsDepartements = new Map();

          lotsDepartementsLibre.forEach((ld: any) => {
            if (ld.lot_technique_id) {
              uniqueLotsTechniques.set(ld.lot_technique_id, {
                id: ld.lot_technique_id,
                nom: ld.lot_technique_nom || ''
              });
            }
            if (ld.departement_id) {
              uniqueLotsDepartements.set(ld.departement_id, {
                id: ld.departement_id,
                nom: ld.departement_nom || ''
              });
            }
          });

          // Stocker les lots de l'entité
          setEntiteLotsTechniques(Array.from(uniqueLotsTechniques.values()));
          setEntiteLotsDepartements(Array.from(uniqueLotsDepartements.values()));

          // Pour les filtres
          setAvailableLotsTechniques(Array.from(uniqueLotsTechniques.values()));
          setAvailableLotsDepartements(Array.from(uniqueLotsDepartements.values()));
          setAvailableContrats([]); // PL n'a pas de contrats

          setUserData(users);
          setFilteredData(users);
          setLoading(false);
          return; // IMPORTANT: Sortir ici pour les PL

        } else if (isPrestataire) {
          // ============================================
          // Traitement pour les prestataires ACCORD CADRE uniquement
          // Utilise /profile pour obtenir l'entité de l'utilisateur connecté
          // puis /interlocuteurs-by-lots pour les lots AC
          // ============================================
          console.log('GestionComptes - Mode Prestataire Accord Cadre');

          // Récupérer le profil pour obtenir l'entité
          const profileResponse = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          let entiteId: number | null = null;
          let entiteNom = '';

          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            if (profileData.status === 'success' && profileData.data?.entite) {
              entiteId = profileData.data.entite.id;
              entiteNom = profileData.data.entite.nom || '';
            }
          }

          // Si pas d'entité via profile, fallback sur /entites (une seule entité)
          if (!entiteId) {
            const entitesResponse = await fetch(`${process.env.REACT_APP_API_URL}/entites`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            });

            if (entitesResponse.ok) {
              const entitesData = await entitesResponse.json();
              if (entitesData.status === 'success' && entitesData.data?.length > 0) {
                // Prendre la première entité (celle de l'utilisateur)
                entiteId = entitesData.data[0].id;
                entiteNom = entitesData.data[0].nom || '';
              }
            }
          }

          if (!entiteId) {
            throw new Error('Aucune entité trouvée pour cet utilisateur');
          }

          // Récupérer les lots de cette entité (un seul appel)
          const lotsResponse = await fetch(`${process.env.REACT_APP_API_URL}/entites/${entiteId}/interlocuteurs-by-lots`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          let lots: any[] = [];
          if (lotsResponse.ok) {
            const lotsData = await lotsResponse.json();
            if (lotsData.status === 'success' && lotsData.data?.lots) {
              lots = lotsData.data.lots;
            }
          }

          // Créer les lignes pour chaque interlocuteur par lot
          for (const lot of lots) {
            for (const interlocuteur of lot.interlocuteurs || []) {
              const rang = interlocuteur.rang || 0;
              users.push({
                id: interlocuteur.id,
                idUser: interlocuteur.id_user,
                nom: `${interlocuteur.prenom} ${interlocuteur.nom}`,
                prenom: interlocuteur.prenom,
                email: interlocuteur.email_contact,
                role: interlocuteur.fonction || 'Prestataire',
                fonction: interlocuteur.fonction || 'Interlocuteur',
                telephone: interlocuteur.telephone || '',
                organisme: entiteNom,
                dateMiseAJour: new Date(interlocuteur.updated_at || interlocuteur.created_at).toLocaleDateString('fr-FR'),
                lotTechnique: lot.lot_technique,
                lotDepartement: lot.departement,
                lotTechniqueId: lot.lot_technique?.id,
                lotDepartementId: lot.departement?.id,
                contratNumero: interlocuteur.contrat_numero || '',
                entiteId: entiteId,
                type: rang > 0 ? `Titulaire rang ${rang}` : 'Référencé',
                rang: rang
              });
            }
          }

          // Extraire pour les filtres
          const uniqueLotsTechniques = new Map();
          const uniqueLotsDepartements = new Map();
          const uniqueContrats = new Set<string>();

          lots.forEach((lot: any) => {
            if (lot.lot_technique?.id) {
              uniqueLotsTechniques.set(lot.lot_technique.id, lot.lot_technique);
            }
            if (lot.departement?.id) {
              uniqueLotsDepartements.set(lot.departement.id, lot.departement);
            }
            for (const interlocuteur of lot.interlocuteurs || []) {
              if (interlocuteur.contrat_numero) {
                uniqueContrats.add(interlocuteur.contrat_numero);
              }
            }
          });

          setAvailableLotsTechniques(Array.from(uniqueLotsTechniques.values()));
          setAvailableLotsDepartements(Array.from(uniqueLotsDepartements.values()));
          setAvailableContrats(Array.from(uniqueContrats));

        } else {
          // Traitement pour les acheteurs (organismes)
          // Récupérer d'abord l'ID de l'organisme de l'utilisateur connecté
          const userEndpoint = userRole === 'Acheteur signataire admin'
            ? 'responsables-achat/me'
            : userRole === 'Acheteur signataire'
              ? 'validateurs-achat/me'
              : 'acheteurs/me';

          const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/${userEndpoint}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!meResponse.ok) {
            throw new Error('Erreur lors de la récupération des informations utilisateur');
          }

          const meData = await meResponse.json();
          const organismeId = meData.data?.id_organisme;

          if (!organismeId) {
            throw new Error('ID d\'organisme non trouvé');
          }

          // Récupérer les informations de l'organisme avec tous ses utilisateurs
          const organismeResponse = await fetch(`${process.env.REACT_APP_API_URL}/organismes/${organismeId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!organismeResponse.ok) {
            throw new Error('Erreur lors de la récupération de l\'organisme');
          }

          const organismeData = await organismeResponse.json();

          if (organismeData.status !== 'success' || !organismeData.data) {
            throw new Error('Format de réponse API inattendu');
          }

          const organisme = organismeData.data;

          // Ajouter les responsables achat (Acheteur signataire admin)
          if (organisme.responsables_achat && organisme.responsables_achat.length > 0) {
            organisme.responsables_achat.forEach((responsable: any) => {
              users.push({
                id: responsable.id,
                idUser: responsable.id_user,
                nom: `${responsable.prenom} ${responsable.nom}`,
                prenom: responsable.prenom,
                email: responsable.email_contact,
                role: 'Acheteur signataire admin',
                fonction: responsable.fonction || '',
                telephone: responsable.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(responsable.updated_at || responsable.created_at).toLocaleDateString('fr-FR')
              });
            });
          }

          // Ajouter les validateurs achat (Acheteur signataire)
          if (organisme.validateurs_achat && organisme.validateurs_achat.length > 0) {
            organisme.validateurs_achat.forEach((validateur: any) => {
              users.push({
                id: validateur.id,
                idUser: validateur.id_user,
                nom: `${validateur.prenom} ${validateur.nom}`,
                prenom: validateur.prenom,
                email: validateur.email_contact,
                role: 'Acheteur signataire',
                fonction: validateur.fonction || '',
                telephone: validateur.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(validateur.updated_at || validateur.created_at).toLocaleDateString('fr-FR')
              });
            });
          }

          // Ajouter les acheteurs (Acheteur)
          if (organisme.acheteurs && organisme.acheteurs.length > 0) {
            organisme.acheteurs.forEach((acheteur: any) => {
              users.push({
                id: acheteur.id,
                idUser: acheteur.id_user,
                nom: `${acheteur.prenom} ${acheteur.nom}`,
                prenom: acheteur.prenom,
                email: acheteur.email_contact,
                role: 'Acheteur',
                fonction: acheteur.fonction || '',
                telephone: acheteur.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(acheteur.updated_at || acheteur.created_at).toLocaleDateString('fr-FR')
              });
            });
          }
        }

        // Filtrer les doublons (garder uniquement le rôle le plus élevé pour chaque utilisateur)
        const uniqueUsers = filterDuplicateUsers(users);

        // Trier les utilisateurs par lot technique, département et numéro de contrat
        const sortedUsers = uniqueUsers.sort((a: any, b: any) => {
          // Si pas de lots (acheteurs), les mettre à la fin
          if (!a.lotTechniqueId && !b.lotTechniqueId) return 0;
          if (!a.lotTechniqueId) return 1;
          if (!b.lotTechniqueId) return -1;

          // Trier par lot technique
          if (a.lotTechniqueId !== b.lotTechniqueId) {
            return a.lotTechniqueId - b.lotTechniqueId;
          }

          // Trier par département
          if (a.lotDepartementId !== b.lotDepartementId) {
            return a.lotDepartementId - b.lotDepartementId;
          }

          // Trier par numéro de contrat
          return (a.contratNumero || '').localeCompare(b.contratNumero || '');
        });

        setUserData(sortedUsers);
        setFilteredData(sortedUsers);

        // Pour les prestataires (AC et libres), extraire les lots techniques, départements et contrats disponibles
        if (isPrestataire || isPrestataireLibre) {
          const lotsTechniquesSet = new Set();
          const lotsDepartementsSet = new Set();
          const contratsSet = new Set();

          sortedUsers.forEach((user: any) => {
            if (user.lotTechnique) {
              lotsTechniquesSet.add(JSON.stringify({ id: user.lotTechniqueId, nom: user.lotTechnique.nom }));
            }
            if (user.lotDepartement) {
              lotsDepartementsSet.add(JSON.stringify({ id: user.lotDepartementId, nom: user.lotDepartement.nom }));
            }
            if (user.contratNumero) {
              contratsSet.add(user.contratNumero);
            }
          });

          setAvailableLotsTechniques(Array.from(lotsTechniquesSet).map((item: any) => JSON.parse(item)));
          setAvailableLotsDepartements(Array.from(lotsDepartementsSet).map((item: any) => JSON.parse(item)));
          setAvailableContrats(Array.from(contratsSet) as string[]);
        }

        // Compter les validateurs
        const validatorsCount = users.filter(user => user.role === 'Acheteur signataire').length;
        setValidatorsCount(validatorsCount);

      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        setError(error instanceof Error ? error.message : 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isPrestataire, isPrestataireLibre]);

  // Appliquer les filtres pour les prestataires
  useEffect(() => {
    if (!isPrestataire && !isPrestataireLibre) return;

    let filtered = [...userData];

    // Filtre par lot technique
    if (lotTechniqueFilter) {
      filtered = filtered.filter(user =>
        user.lotTechniqueId === parseInt(lotTechniqueFilter)
      );
    }

    // Filtre par lot département
    if (lotDepartementFilter) {
      filtered = filtered.filter(user =>
        user.lotDepartementId === parseInt(lotDepartementFilter)
      );
    }

    // Filtre par N° Contrat
    if (contratFilter) {
      filtered = filtered.filter(user =>
        user.contratNumero === contratFilter
      );
    }

    setFilteredData(filtered);
  }, [lotTechniqueFilter, lotDepartementFilter, contratFilter, userData, isPrestataire]);

  // Fonction pour récupérer les gestionnaires actifs depuis userData
  const getGestionnairesActifs = () => {
    if (isPrestataire || isPrestataireLibre) {
      // Filtrer les utilisateurs avec le rôle "Prestataire signataire admin" (gestionnaire)
      return userData.filter(u => u.role === 'Prestataire signataire admin');
    } else {
      // Filtrer les utilisateurs avec le rôle "Acheteur signataire admin" (gestionnaire)
      return userData.filter(u => u.role === 'Acheteur signataire admin');
    }
  };

  // Fonction pour ouvrir le modal de changement de rôle
  const handleRoleClick = (userRow: any) => {
    // Vérifier si l'utilisateur connecté a le droit de modifier les rôles
    // et si le rôle de l'utilisateur à modifier n'est pas un rôle admin
    if (!isAdmin ||
      userRow.role === 'Acheteur signataire admin' ||
      userRow.role === 'Prestataire signataire admin' ||
      userRow.role.toLowerCase().includes('admin')) {
      return;
    }

    setSelectedUser(userRow);
    setIsChangeRoleModalOpen(true);
  };

  // Fonction pour gérer le changement de rôle
  const handleChangeRole = async (newRole: string) => {
    if (!selectedUser) return;

    try {
      // Vérification du nombre de validateurs si on veut passer à validateur (Acheteur)
      if (newRole === 'Acheteur signataire') {
        // Compter les validateurs actuels (hors l'utilisateur sélectionné)
        const currentValidators = userData.filter(
          u => u.role === 'Acheteur signataire' && u.id !== selectedUser.id
        ).length;

        // Si on a déjà 2 validateurs, empêcher l'ajout d'un autre
        if (currentValidators >= 2) {
          alert('Vous ne pouvez pas avoir plus de 2 validateurs par organisme.');
          return;
        }
      }

      // Vérification du nombre de gestionnaires si on veut passer à gestionnaire
      if (newRole === 'Prestataire signataire admin' || newRole === 'Acheteur signataire admin') {
        // Compter les gestionnaires actuels (hors l'utilisateur sélectionné)
        const currentGestionnaires = getGestionnairesActifs().filter(
          u => u.id !== selectedUser.id
        );

        // Si on a déjà 3 gestionnaires, déclencher le processus de passation
        if (currentGestionnaires.length >= 3) {
          console.log('Limite de 3 gestionnaires atteinte, ouverture du modal de passation');
          setGestionnairesActifs(currentGestionnaires);
          setPendingPromotion({ user: selectedUser, newRole });
          setIsPassationModalOpen(true);
          setIsChangeRoleModalOpen(false);
          return;
        }
      }

      console.log(`Changing role for user ${selectedUser.id} to ${newRole}`);

      // Obtenir le rôle API correspondant
      const roleForApi = convertUiRoleToApiRole(newRole);

      // Déterminer la route API appropriée selon le rôle actuel et le nouveau rôle
      let apiRoute = '';
      const token = localStorage.getItem('token');

      // L'implémentation exacte dépendra de l'API disponible pour changer le rôle
      // Voici une approche possible basée sur les routes existantes
      await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedUser.idUser}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ role: roleForApi })
      });

      // Mettre à jour les données localement
      const updatedData = userData.map(user => {
        if (user.id === selectedUser.id) {
          return { ...user, role: newRole };
        }
        return user;
      });

      setUserData(updatedData);
      setFilteredData(updatedData);

      // Fermer le modal après changement
      setIsChangeRoleModalOpen(false);
      setTempSelectedRole(null);

      // Recharger les données pour s'assurer qu'elles sont à jour
      window.location.reload();

    } catch (error) {
      console.error('Error updating role:', error);
      alert("Une erreur est survenue lors du changement de rôle.");
    }
  };

  // Fonction pour confirmer la passation de gestionnaire
  const handleConfirmPassation = async (gestionnaireSortantId: number) => {
    if (!pendingPromotion) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Token d'authentification non trouvé");

      // Appeler l'API pour initier la passation
      const response = await fetch(`${process.env.REACT_APP_API_URL}/gestionnaires/initiate-passation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nouveau_gestionnaire_id: pendingPromotion.user.idUser,
          gestionnaire_sortant_id: gestionnaireSortantId,
          nouveau_role: convertUiRoleToApiRole(pendingPromotion.newRole)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'initiation de la passation');
      }

      const data = await response.json();

      // Afficher un message de succès
      alert(`Un email de validation a été envoyé au gestionnaire sortant. La passation sera effective après validation.`);

      // Fermer le modal et réinitialiser les états
      setIsPassationModalOpen(false);
      setPendingPromotion(null);
      setGestionnairesActifs([]);

      // Recharger les données
      window.location.reload();

    } catch (error: any) {
      console.error('Erreur lors de la passation:', error);
      alert(error.message || "Une erreur est survenue lors de l'initiation de la passation.");
    }
  };

  const handleSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredData(userData);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = userData.filter(item =>
      item.nom.toLowerCase().includes(searchTermLower) ||
      item.email.toLowerCase().includes(searchTermLower) ||
      item.organisme.toLowerCase().includes(searchTermLower)
    );

    setFilteredData(filtered);
  };

  const handleExport = () => {
    // Implémentation de l'export (CSV, Excel, etc.)
    console.log('Exporting user data...');

    // Exemple: Création d'un CSV basique
    const headers = ["Nom", "Email", "Rôle", "Fonction", "Organisme", "Date de mise à jour"];
    const csvData = [
      headers.join(","),
      ...filteredData.map(user => [
        `"${user.nom}"`,
        `"${user.email}"`,
        `"${user.role}"`,
        `"${user.fonction || ''}"`,
        `"${user.organisme || ''}"`,
        `"${user.dateMiseAJour || ''}"`,
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'utilisateurs.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFilter = () => {
    console.log('Filtering...');
  };

  const handleCreateCompte = async (formData: any) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error("Token d'authentification non trouvé");

      // Vérification du nombre de signataires si on veut créer un Acheteur signataire
      if (formData.role === 'acheteur_signataire') {
        // Compter les signataires actuels
        const currentSignataires = userData.filter(
          u => u.role === 'Acheteur signataire'
        ).length;

        // Si on a déjà 3 signataires, empêcher l'ajout d'un autre
        if (currentSignataires >= 3) {
          alert('Vous ne pouvez pas avoir plus de 3 signataires par organisme.');
          return;
        }
      }

      // Générer un mot de passe aléatoire sécurisé
      const generateRandomPassword = () => {
        const length = 16;
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
        let password = '';
        for (let i = 0; i < length; i++) {
          const randomIndex = Math.floor(Math.random() * charset.length);
          password += charset[randomIndex];
        }
        return password;
      };

      const randomPassword = generateRandomPassword();

      // Déterminer le rôle API et l'endpoint en fonction du rôle sélectionné
      let apiRole = 'acheteur';
      let apiEndpoint = 'acheteurs';

      if (formData.role === 'acheteur_signataire') {
        apiRole = 'signataire'; // Le rôle dans la table users est "signataire"
        apiEndpoint = 'validateurs-achat'; // Mais l'endpoint API est "validateurs-achat"
      } else if (formData.role === 'acheteur') {
        apiRole = 'acheteur';
        apiEndpoint = 'acheteurs';
      }

      // DEBUG: Vérifier le token utilisé
      console.log('🔑 Token utilisé pour la création:', token?.substring(0, 20) + '...');
      console.log('👤 User courant:', user);
      console.log('📧 Création du compte:', formData.email, 'avec rôle:', apiRole);

      // Étape 1: Créer l'utilisateur dans la table users avec le token de l'admin
      const registerResponse = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase(),
          password: randomPassword,
          password_confirmation: randomPassword,
          telephone: formData.telephone,
          role: apiRole
        }),
      });

      const registerData = await registerResponse.json();
      if (registerData.status !== 'success') {
        throw new Error(registerData.message || "Erreur lors de la création de l'utilisateur");
      }

      // Étape 2: Récupérer l'ID de l'organisme de l'utilisateur connecté
      const currentUserResponse = await fetch(`${process.env.REACT_APP_API_URL}/responsables-achat/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      const currentUserData = await currentUserResponse.json();
      if (currentUserData.status !== 'success' || !currentUserData.data?.id_organisme) {
        throw new Error("Impossible de récupérer l'organisme de l'utilisateur connecté");
      }

      const organismeId = currentUserData.data.id_organisme;

      // Étape 3: Créer l'entrée avec le bon endpoint selon le rôle
      const profileResponse = await fetch(`${process.env.REACT_APP_API_URL}/${apiEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}` // ✅ Utiliser le token de l'admin connecté
        },
        body: JSON.stringify({
          id_organisme: organismeId,
          id_user: registerData.user.id,
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          email_contact: formData.email.toLowerCase(),
          fonction: formData.fonction || ''
        }),
      });

      const profileData = await profileResponse.json();
      if (profileData.status !== 'success') {
        throw new Error(profileData.message || `Erreur lors de la création du profil ${apiRole}`);
      }

      const roleLabel = formData.role === 'acheteur_signataire' ? 'Acheteur signataire' : 'Acheteur';
      alert(`Compte ${roleLabel} créé avec succès pour ${formData.prenom} ${formData.nom}!\n\nUn email a été envoyé à ${formData.email} avec les instructions de connexion.`);

      // Recharger TOUTES les données de l'organisme (pas seulement les acheteurs)
      // Ceci permet de voir les signataires également
      const organismeResponse = await fetch(`${process.env.REACT_APP_API_URL}/organismes/${organismeId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (organismeResponse.ok) {
        const organismeData = await organismeResponse.json();

        if (organismeData.status === 'success' && organismeData.data) {
          const organisme = organismeData.data;
          const users: any[] = [];

          // Ajouter les responsables achat (Acheteur signataire admin)
          if (organisme.responsables_achat && organisme.responsables_achat.length > 0) {
            organisme.responsables_achat.forEach((responsable: any) => {
              users.push({
                id: responsable.id,
                idUser: responsable.id_user,
                nom: `${responsable.prenom} ${responsable.nom}`,
                prenom: responsable.prenom,
                email: responsable.email_contact,
                role: 'Acheteur signataire admin',
                fonction: responsable.fonction || '',
                telephone: responsable.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(responsable.updated_at || responsable.created_at).toLocaleDateString('fr-FR')
              });
            });
          }

          // Ajouter les validateurs achat (Acheteur signataire)
          if (organisme.validateurs_achat && organisme.validateurs_achat.length > 0) {
            organisme.validateurs_achat.forEach((validateur: any) => {
              users.push({
                id: validateur.id,
                idUser: validateur.id_user,
                nom: `${validateur.prenom} ${validateur.nom}`,
                prenom: validateur.prenom,
                email: validateur.email_contact,
                role: 'Acheteur signataire',
                fonction: validateur.fonction || '',
                telephone: validateur.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(validateur.updated_at || validateur.created_at).toLocaleDateString('fr-FR')
              });
            });
          }

          // Ajouter les acheteurs (Acheteur)
          if (organisme.acheteurs && organisme.acheteurs.length > 0) {
            organisme.acheteurs.forEach((acheteur: any) => {
              users.push({
                id: acheteur.id,
                idUser: acheteur.id_user,
                nom: `${acheteur.prenom} ${acheteur.nom}`,
                prenom: acheteur.prenom,
                email: acheteur.email_contact,
                role: 'Acheteur',
                fonction: acheteur.fonction || '',
                telephone: acheteur.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateMiseAJour: new Date(acheteur.updated_at || acheteur.created_at).toLocaleDateString('fr-FR')
              });
            });
          }

          setUserData(users);
          setFilteredData(users);
        }
      }

      setIsCreateModalOpen(false);

    } catch (error) {
      console.error('Erreur lors de la création du compte:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  const handleEditUser = (userId: string | any) => {
    // Si c'est un objet (row), extraire l'id
    const actualUserId = typeof userId === 'object' ? userId.id : userId;
    console.log(`Modifier les informations de l'utilisateur ${actualUserId}`);
    const user = userData.find(u => u.id === actualUserId);
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleSaveUser = async (userId: string, updatedUserData: { nom: string; prenom: string; email: string; fonction: string; role?: string }) => {
    try {
      if (!selectedUser) return false;

      const token = localStorage.getItem('token');
      const roleChanged = updatedUserData.role && updatedUserData.role !== selectedUser.role;
      const oldRole = selectedUser.role;
      const newRole = updatedUserData.role;

      // Mapping des rôles vers les endpoints API
      const roleToEndpoint: { [key: string]: string } = {
        'Acheteur signataire admin': 'responsables-achat',
        'Acheteur signataire': 'validateurs-achat',
        'Acheteur': 'acheteurs',
        'Prestataire signataire admin': 'gestionnaires',
        'Prestataire signataire': 'signataires',
        'Interlocuteur': 'interlocuteurs'
      };

      // Variable pour stocker l'id_organisme récupéré pendant le changement de rôle
      let fetchedOrganismeId: number | string | null = null;

      // Si le rôle a changé, c'est une promotion/rétrogradation
      // Il faut créer un nouvel enregistrement dans la nouvelle table et supprimer l'ancien
      if (roleChanged && newRole) {
        console.log('Promotion/Changement de rôle détecté:', oldRole, '->', newRole);

        // Déterminer le type de changement
        const isAcheteurPromotion = oldRole === 'Acheteur' && (newRole === 'Acheteur signataire' || newRole === 'Acheteur signataire admin');
        const isAcheteurDemote = (oldRole === 'Acheteur signataire' || oldRole === 'Acheteur signataire admin') && newRole === 'Acheteur';

        // 1. Récupérer l'id_user depuis l'ancien enregistrement
        const oldEndpoint = roleToEndpoint[oldRole];
        const oldRecordResponse = await fetch(`${process.env.REACT_APP_API_URL}/${oldEndpoint}/${selectedUser.id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!oldRecordResponse.ok) {
          throw new Error(`Erreur lors de la récupération de l'ancien enregistrement: ${oldRecordResponse.status}`);
        }

        const oldRecordData = await oldRecordResponse.json();
        const idUser = oldRecordData.data?.id_user;
        const idOrganisme = oldRecordData.data?.id_organisme;

        // Stocker l'id_organisme pour le rechargement ultérieur
        fetchedOrganismeId = idOrganisme;

        if (!idUser || !idOrganisme) {
          throw new Error('Impossible de récupérer id_user ou id_organisme');
        }

        console.log('Données utilisateur récupérées:', { idUser, idOrganisme });

        // DEMOTE (Acheteur signataire -> Acheteur) : 
        // On supprime juste l'entrée validateur, le compte acheteur existe déjà
        if (isAcheteurDemote) {
          console.log('Demote détecté - Suppression de l\'entrée validateur uniquement');

          const deleteResponse = await fetch(`${process.env.REACT_APP_API_URL}/${oldEndpoint}/${selectedUser.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (!deleteResponse.ok) {
            const errorData = await deleteResponse.json().catch(() => ({}));
            console.error('Erreur suppression:', errorData);
            throw new Error(`Erreur lors du demote: ${deleteResponse.status}`);
          }

          console.log('Demote réussi - Le compte acheteur existant est conservé');

        } else {
          // PROMOTION ou autre changement : créer le nouvel enregistrement
          const newEndpoint = roleToEndpoint[newRole];
          const createData = {
            id_user: idUser,
            id_organisme: idOrganisme,
            nom: updatedUserData.nom,
            prenom: updatedUserData.prenom,
            email_contact: updatedUserData.email.toLowerCase(),
            fonction: updatedUserData.fonction,
            telephone: oldRecordData.data?.telephone || ''
          };

          console.log('Création du nouvel enregistrement:', newEndpoint, createData);

          const createResponse = await fetch(`${process.env.REACT_APP_API_URL}/${newEndpoint}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(createData)
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.json().catch(() => ({}));
            console.error('Erreur création:', errorData);
            throw new Error(`Erreur lors de la création du nouveau rôle: ${createResponse.status}`);
          }

          // Supprimer l'ancien enregistrement - SAUF pour les promotions acheteur
          // On garde l'entrée acheteur car l'id_acheteur est lié aux commandes (historique)
          if (!isAcheteurPromotion) {
            console.log('Suppression de l\'ancien enregistrement:', oldEndpoint, selectedUser.id);

            const deleteResponse = await fetch(`${process.env.REACT_APP_API_URL}/${oldEndpoint}/${selectedUser.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            });

            if (!deleteResponse.ok) {
              console.warn('Attention: Suppression de l\'ancien enregistrement échouée:', deleteResponse.status);
              // On ne fait pas échouer la requête car le nouveau rôle est déjà créé
            }
          } else {
            console.log('Promotion acheteur détectée - Conservation de l\'entrée acheteur pour préserver l\'historique des commandes');
          }
        }

      } else {
        // Pas de changement de rôle, simple mise à jour
        const currentRole = selectedUser.role;
        const endpoint = roleToEndpoint[currentRole];

        if (!endpoint) {
          throw new Error(`Rôle non pris en charge: ${currentRole}`);
        }

        const apiRoute = `${process.env.REACT_APP_API_URL}/${endpoint}/${selectedUser.id}`;

        // Structurer les données selon l'API
        const requestData = {
          nom: updatedUserData.nom,
          prenom: updatedUserData.prenom,
          email_contact: updatedUserData.email.toLowerCase(),
          fonction: updatedUserData.fonction
        };

        const response = await fetch(apiRoute, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          throw new Error(`Erreur API: ${response.status}`);
        }
      }

      // Si le rôle a changé, recharger les données depuis l'API au lieu de recharger la page
      if (roleChanged) {
        console.log('Rôle changé, rechargement des données...');

        // Utiliser l'id_organisme récupéré pendant le changement de rôle, sinon chercher dans localStorage
        let reloadOrganismeId: string | null = fetchedOrganismeId?.toString() || localStorage.getItem('id_organisme') || localStorage.getItem('id_entite');

        // Dernier recours: récupérer depuis /me
        if (!reloadOrganismeId) {
          try {
            const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/responsables-achat/me`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            });
            if (meResponse.ok) {
              const meData = await meResponse.json();
              reloadOrganismeId = meData.data?.id_organisme?.toString();
            }
          } catch (e) {
            console.warn('Impossible de récupérer id_organisme depuis /me');
          }
        }

        if (!reloadOrganismeId) {
          // Si toujours pas d'ID, recharger la page comme fallback
          console.warn('ID organisme non trouvé, rechargement de la page...');
          window.location.reload();
          return true;
        }

        const loadResponse = await fetch(`${process.env.REACT_APP_API_URL}/organismes/${reloadOrganismeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!loadResponse.ok) {
          throw new Error('Erreur lors du rechargement des données');
        }

        const organismeDataResponse = await loadResponse.json();
        console.log('Données rechargées:', organismeDataResponse);

        // Vérifier que la réponse est valide
        if (organismeDataResponse.status !== 'success' || !organismeDataResponse.data) {
          throw new Error('Réponse invalide lors du rechargement');
        }

        const organismeData = organismeDataResponse.data;

        // Reconstruire la liste complète des utilisateurs
        const allUsers: any[] = [];

        // Ajouter les responsables
        if (organismeData.responsables_achat && Array.isArray(organismeData.responsables_achat)) {
          organismeData.responsables_achat.forEach((responsable: any) => {
            allUsers.push({
              id: responsable.id,
              idUser: responsable.id_user,
              nom: `${responsable.prenom} ${responsable.nom}`,
              prenom: responsable.prenom,
              email: responsable.email_contact,
              telephone: responsable.telephone,
              fonction: responsable.fonction,
              role: 'Acheteur signataire admin',
              derniere_mise_a_jour: responsable.updated_at
            });
          });
        }

        // Ajouter les validateurs
        if (organismeData.validateurs_achat && Array.isArray(organismeData.validateurs_achat)) {
          organismeData.validateurs_achat.forEach((validateur: any) => {
            allUsers.push({
              id: validateur.id,
              idUser: validateur.id_user,
              nom: `${validateur.prenom} ${validateur.nom}`,
              prenom: validateur.prenom,
              email: validateur.email_contact,
              telephone: validateur.telephone,
              fonction: validateur.fonction,
              role: 'Acheteur signataire',
              derniere_mise_a_jour: validateur.updated_at
            });
          });
        }

        // Ajouter les acheteurs
        if (organismeData.acheteurs && Array.isArray(organismeData.acheteurs)) {
          organismeData.acheteurs.forEach((acheteur: any) => {
            allUsers.push({
              id: acheteur.id,
              idUser: acheteur.id_user,
              nom: `${acheteur.prenom} ${acheteur.nom}`,
              prenom: acheteur.prenom,
              email: acheteur.email_contact,
              telephone: acheteur.telephone,
              fonction: acheteur.fonction,
              role: 'Acheteur',
              derniere_mise_a_jour: acheteur.updated_at
            });
          });
        }

        // Filtrer les doublons (garder uniquement le rôle le plus élevé pour chaque utilisateur)
        const uniqueUsers = filterDuplicateUsers(allUsers);

        console.log('Liste complète rechargée (sans doublons):', uniqueUsers);
        setUserData(uniqueUsers);
        setFilteredData(uniqueUsers);
        setIsEditUserModalOpen(false);

        return true;
      }

      // Mise à jour locale des données
      const updatedData = userData.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            nom: `${updatedUserData.prenom} ${updatedUserData.nom}`,
            prenom: updatedUserData.prenom,
            email: updatedUserData.email,
            fonction: updatedUserData.fonction,
            role: updatedUserData.role || user.role,
            dateMiseAJour: new Date().toLocaleDateString('fr-FR')
          };
        }
        return user;
      });

      setUserData(updatedData);
      setFilteredData(updatedData);
      setIsEditUserModalOpen(false);

      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      throw error;
    }
  };

  const handleResetPassword = (userId: string | any) => {
    // Si c'est un objet (row), extraire l'id
    const actualUserId = typeof userId === 'object' ? userId.id : userId;
    console.log(`Réinitialiser le mot de passe pour l'utilisateur ${actualUserId}`);
    setSelectedUser(userData.find(user => user.id === actualUserId));
    setIsResetPasswordModalOpen(true);
  };

  // Fonction pour confirmer la réinitialisation du mot de passe
  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email: selectedUser.email.toLowerCase() })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la réinitialisation du mot de passe');
      }

      alert(`Un email de réinitialisation a été envoyé à ${selectedUser.email}`);
      setIsResetPasswordModalOpen(false);

    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur: ${error instanceof Error ? error.message : 'Une erreur est survenue'}`);
    }
  };

  // Fonction pour ouvrir la modal de réattribution
  const handleOpenReassignModal = async (row: any) => {
    setSelectedLotContrat(row);
    setNewInterlocuteurId(''); // Réinitialiser la sélection

    // Récupérer tous les interlocuteurs de l'entité
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/entites/${row.entiteId}/all-interlocuteurs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Interlocuteurs récupérés:', data);
        if (data.status === 'success' && data.data) {
          setAvailableInterlocuteurs(data.data);
        }
      } else {
        console.error('Erreur HTTP:', response.status);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des interlocuteurs:', error);
    }

    setIsReassignModalOpen(true);
  };

  // Fonction pour réattribuer un interlocuteur à un lot_contrat
  const handleReassignInterlocuteur = async () => {
    if (!selectedLotContrat || !newInterlocuteurId) {
      alert('Veuillez sélectionner un interlocuteur');
      return;
    }

    // Empêcher les appels multiples
    if (isReassigning) {
      return;
    }

    setIsReassigning(true);

    try {
      const token = localStorage.getItem('token');

      // Trouver le lot_contrat_id à partir des informations du row
      // Pour cela, il faut utiliser l'API /entites/{id}/interlocuteurs-by-lots
      // et chercher le lot_contrat qui correspond au lot technique + département + contrat

      const entiteResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/entites/${selectedLotContrat.entiteId}/interlocuteurs-by-lots`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (!entiteResponse.ok) {
        throw new Error('Erreur lors de la récupération des lots-contrats');
      }

      const entiteData = await entiteResponse.json();

      // Trouver le lot_contrat_id correspondant
      let lotContratId = null;
      if (entiteData.status === 'success' && entiteData.data?.lots) {
        const matchingLot = entiteData.data.lots.find((lot: any) => {
          return lot.lot_technique.id === selectedLotContrat.lotTechniqueId &&
            lot.departement.id === selectedLotContrat.lotDepartementId;
        });

        if (matchingLot?.interlocuteurs) {
          const matchingInterlocuteur = matchingLot.interlocuteurs.find((inter: any) =>
            inter.id === parseInt(selectedLotContrat.id)
          );
          lotContratId = matchingInterlocuteur?.lot_contrat_id;
        }
      }

      if (!lotContratId) {
        throw new Error('Impossible de trouver le lot_contrat_id');
      }

      // Appel API pour mettre à jour le lot_contrat
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/interlocuteurs/${selectedLotContrat.id}/reassign`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id_interlocuteur: newInterlocuteurId,
            id_lot_contrat: lotContratId
          })
        }
      );

      if (response.ok) {
        alert('Interlocuteur réattribué avec succès');
        setIsReassignModalOpen(false);
        setNewInterlocuteurId('');
        // Stocker l'onglet "Gestion d'équipe" pour y revenir après le rechargement
        localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
        // Recharger les données
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la réattribution');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de la réattribution: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
      setIsReassigning(false);
    }
  };

  // ============================================
  // FRONT-16: Fonctions pour l'attribution de MEMBRE à un LOT (Prestataires Libres)
  // Logique inversée: La grille affiche les LOTS, le modal permet de choisir un MEMBRE
  // ============================================

  // Ouvrir la modale d'attribution de membre pour un lot PL
  const handleOpenLotAssignModal = (row: any) => {
    setSelectedLotForMember(row);
    // Pré-sélectionner le membre actuel si le lot est déjà attribué
    setSelectedMemberId(row.assignedMemberId?.toString() || '');
    setIsLotAssignModalOpen(true);
  };

  // Sauvegarder l'attribution de membre à un lot
  const handleSaveLotAssignment = async () => {
    if (!selectedLotForMember || !selectedMemberId) {
      alert('Veuillez sélectionner un membre');
      return;
    }

    setIsAssigningLot(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile/team/${selectedMemberId}/lots`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            lot_technique_id: selectedLotForMember.lotTechniqueId,
            lot_departement_id: selectedLotForMember.lotDepartementId
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Membre assigné avec succès');
        setIsLotAssignModalOpen(false);
        // Stocker l'onglet "Gestion d'équipe" pour y revenir après le rechargement
        localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
        // Recharger les données
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'attribution');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de l'attribution: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsAssigningLot(false);
    }
  };

  // Retirer l'attribution d'un membre d'un lot
  const handleRemoveLotAssignment = async () => {
    if (!selectedLotForMember || !selectedLotForMember.lotAssignmentId) {
      alert('Aucune attribution à retirer');
      return;
    }

    if (!window.confirm('Êtes-vous sûr de vouloir retirer l\'attribution de ce lot ?')) {
      return;
    }

    setIsAssigningLot(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/profile/team/${selectedLotForMember.assignedMemberId}/lots/${selectedLotForMember.lotAssignmentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        alert('Attribution retirée avec succès');
        setIsLotAssignModalOpen(false);
        localStorage.setItem('activeProfilTab', 'Gestion d\'équipe');
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors du retrait');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors du retrait: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    } finally {
      setIsAssigningLot(false);
    }
  };

  // Fonction pour supprimer un utilisateur
  const handleDeleteUser = (userId: string | any) => {
    // Si c'est un objet (row), extraire l'id
    const actualUserId = typeof userId === 'object' ? userId.id : userId;

    // Confirmer la suppression
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      console.log(`Supprimer l'utilisateur ${actualUserId}`);
      // Implémentation de la suppression via API
      const token = localStorage.getItem('token');
      // Trouver l'utilisateur pour déterminer son rôle et la bonne route API
      const userToDelete = userData.find(user => user.id === actualUserId);
      if (!userToDelete) return;

      // Déterminer la bonne route API en fonction du rôle de l'utilisateur à supprimer
      let apiRoute = '';
      switch (userToDelete.role) {
        case 'Prestataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/interlocuteurs/${actualUserId}`;
          break;
        case 'Prestataire signataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/signataires/${actualUserId}`;
          break;
        case 'Acheteur':
          apiRoute = `${process.env.REACT_APP_API_URL}/acheteurs/${actualUserId}`;
          break;
        case 'Acheteur signataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/validateurs-achat/${actualUserId}`;
          break;
        default:
          alert("Type d'utilisateur non pris en charge pour la suppression");
          return;
      }

      // Appel API pour supprimer l'utilisateur
      fetch(apiRoute, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(response => {
          if (!response.ok) throw new Error('Erreur lors de la suppression');
          // Mettre à jour la liste d'utilisateurs localement
          const updatedData = userData.filter(user => user.id !== actualUserId);
          setUserData(updatedData);
          setFilteredData(updatedData);
          alert("Utilisateur supprimé avec succès");
        })
        .catch(error => {
          console.error('Erreur:', error);
          alert("Erreur lors de la suppression de l'utilisateur");
        });
    }
  };

  // Fonction qui renvoie l'icône appropriée pour les actions de suppression
  const getDeleteIcon = () => {
    return <CrossIcon />;
  };

  // Définir les options d'action pour chaque ligne en fonction du rôle de l'utilisateur
  const getActionOptions = (row: any) => {
    const userId = row.id;
    const rowRole = row.role;

    // Si l'utilisateur connecté est un prestataire admin (Gestionnaire)
    if (userRole === 'Prestataire signataire admin') {
      // Les options disponibles dépendent du rôle de l'utilisateur dans la ligne
      if (rowRole === 'Prestataire signataire admin') {
        // Un gestionnaire ne peut pas modifier un autre gestionnaire
        return [];
      } else if (rowRole === 'Prestataire signataire') {
        // Pour un signataire, on peut modifier, réinitialiser et supprimer
        return [
          {
            label: 'Modifier les informations',
            onClick: () => handleEditUser(userId),
            icon: <UserEditIcon />
          },
          {
            label: 'Réinitialiser le mot de passe',
            onClick: () => handleResetPassword(userId),
            icon: <KeyIcon />
          },
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      } else if (rowRole === 'Prestataire') {
        // Pour un interlocuteur, on peut modifier, promouvoir, réinitialiser et supprimer
        return [
          {
            label: 'Modifier les informations',
            onClick: () => handleEditUser(userId),
            icon: <UserEditIcon />
          },
          {
            label: 'Promouvoir',
            onClick: () => handleRoleClick(row),
            icon: <EditIcon />
          },
          {
            label: 'Réinitialiser le mot de passe',
            onClick: () => handleResetPassword(userId),
            icon: <KeyIcon />
          },
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      }
    }
    // Si l'utilisateur connecté est un prestataire signataire
    else if (userRole === 'Prestataire signataire') {
      // Un signataire peut uniquement supprimer des interlocuteurs
      if (rowRole === 'Prestataire') {
        return [
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      }
      return [];
    }

    // Pour les autres rôles (acheteurs), comportement par défaut
    return [
      {
        label: 'Modifier les informations',
        onClick: () => handleEditUser(userId),
        icon: <UserEditIcon />
      },
      {
        label: 'Réinitialiser le mot de passe',
        onClick: () => handleResetPassword(userId),
        icon: <KeyIcon />
      }
    ];
  };

  const columns = [
    // Colonnes Lots Techniques et Départements (uniquement pour prestataires)
    ...((isPrestataire || isPrestataireLibre) ? [
      {
        field: 'lotTechnique',
        header: 'Lot Technique',
        renderCell: (value: any) => {
          if (!value) {
            return <div className="no-lot">-</div>;
          }
          const shortName = value.nom.substring(0, 8);
          return (
            <div className="lot-technique" title={`${value.id} - ${value.nom}`}>
              {value.id} - {shortName}
            </div>
          );
        }
      },
      {
        field: 'lotDepartement',
        header: 'Département',
        renderCell: (value: any) => {
          if (!value) {
            return <div className="no-lot">-</div>;
          }
          const shortName = value.nom.substring(0, 8);
          return (
            <div className="lot-departement" title={`${String(value.id).padStart(2, '0')} - ${value.nom}`}>
              {String(value.id).padStart(2, '0')} - {shortName}
            </div>
          );
        }
      },
      // FRONT-16: Colonne N° Contrat uniquement pour AC (pas pour PL)
      ...(!isPrestataireLibre ? [{
        field: 'contratNumero',
        header: 'N° Contrat',
        renderCell: (value: string) => {
          if (!value) {
            return <div className="no-lot">-</div>;
          }
          return (
            <div className="contrat-numero">
              {value}
            </div>
          );
        }
      }] : []),
      {
        field: 'type',
        header: 'Type',
        renderCell: (value: string) => (
          <div className="type-cell">
            {value || '-'}
          </div>
        )
      }
    ] : []),
    {
      field: 'nom',
      header: 'Nom / Prénom',
      renderCell: (value: string) => (
        <div className="user-name">
          {value}
        </div>
      )
    },
    {
      field: 'email',
      header: 'Email',
      renderCell: (value: string) => (
        <div className="user-email">
          {value}
        </div>
      )
    },
    ...((isPrestataire || isPrestataireLibre) ? [
      {
        field: 'telephone',
        header: 'Téléphone',
        renderCell: (value: string) => (
          <div className="user-phone">
            {value || '-'}
          </div>
        )
      }
    ] : []),
    // Si c'est un prestataire, afficher téléphone au lieu de nombre de commande
    // {
    //   field: isPrestataire ? 'telephone' : 'nombreCommande',
    //   header: isPrestataire ? 'Téléphone' : 'Nombre de commande',
    //   renderCell: (value: any) => (
    //     <div className={isPrestataire ? "user-phone" : "commande-count"}>
    //       {isPrestataire ? (value || '-') : value}
    //     </div>
    //   )
    // },
    // // Pour les prestataires, afficher la fonction au lieu des valeurs de commande
    // {
    //   field: isPrestataire ? 'fonction' : 'valeurCommande',
    //   header: isPrestataire ? 'Fonction' : 'Valeurs de commande',
    //   renderCell: (value: any) => (
    //     <div className={isPrestataire ? "user-fonction" : "commande-value"}>
    //       {isPrestataire ? (value || '-') : `${value.toLocaleString('fr-FR')} €`}
    //     </div>
    //   )
    // },
    {
      field: 'role',
      header: 'Rôle',
      renderCell: (value: string, row: any) => {
        if (!row) {
          return <div className="user-role">{value}</div>;
        }

        // Pour les prestataires, désactiver COMPLÈTEMENT la modification des rôles
        // Aucun prestataire (même admin) ne peut modifier les rôles
        const isRoleEditable = false; // Toujours false pour les prestataires

        return (
          <div
            className="user-role"
            style={{
              cursor: 'default',
              textDecoration: 'none',
              opacity: 0.8
            }}
          >
            <span>{value}</span>
          </div>
        );
      }
    },
    {
      field: 'dateMiseAJour',
      header: 'Date de mise à jour',
      renderCell: (value: string) => (
        <div className="creation-date">
          {value}
        </div>
      )
    },
    // Colonne Actions pour les prestataires et les acheteurs admins
    ...((isPrestataire || isPrestataireLibre || (isClient && isAdminRole(userRole))) ? [
      {
        field: 'actions',
        header: 'Actions',
        renderCell: (value: any, row: any) => {
          // FRONT-16: Pour les Prestataires Libres : bouton pour modifier le membre assigné au lot
          if (isPrestataireLibre) {
            // Seul le Prestataire Libre Admin peut modifier les affectations
            const canModify = isAdmin;

            if (canModify) {
              return (
                <div className="actions-cell">
                  <Button
                    value="Modifier"
                    variant="secondary"
                    size="small"
                    icon={<EditIcon />}
                    onClick={() => handleOpenLotAssignModal(row)}
                  />
                </div>
              );
            } else {
              return (
                <div className="actions-cell" style={{ color: '#999', fontStyle: 'italic' }}>
                  -
                </div>
              );
            }
          }

          // Pour les prestataires AC : bouton "Modifier" pour réattribution (seulement pour admin)
          if (isPrestataire) {
            // Seul le Prestataire signataire admin peut modifier les affectations
            const canModify = userRole === 'Prestataire signataire admin';

            if (canModify) {
              return (
                <div className="actions-cell">
                  <Button
                    value="Modifier"
                    variant="secondary"
                    size="small"
                    icon={<EditIcon />}
                    onClick={() => handleOpenReassignModal(row)}
                  />
                </div>
              );
            } else {
              return (
                <div className="actions-cell" style={{ color: '#999', fontStyle: 'italic' }}>
                  Non autorisé
                </div>
              );
            }
          }

          // Pour les acheteurs admins : boutons Modifier/MDP/Supprimer pour les acheteurs simples
          if (isClient && isAdminRole(userRole)) {
            // Ne pas afficher les actions pour les admins
            if (row.role === 'Acheteur signataire admin') {
              return (
                <div className="actions-cell" style={{ color: '#999', fontStyle: 'italic' }}>
                  Contactez Centralis pour modification
                </div>
              );
            }

            // Afficher les actions pour les autres rôles
            return (
              <div className="actions-cell" style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  value="Modifier"
                  variant="secondary"
                  size="small"
                  icon={<EditIcon />}
                  onClick={() => handleEditUser(row)}
                />
                <Button
                  value="MDP"
                  variant="secondary"
                  size="small"
                  icon={<KeyIcon />}
                  onClick={() => handleResetPassword(row)}
                />
              </div>
            );
          }

          return null;
        }
      }
    ] : [])
  ];

  if (loading) {
    return <div className="loading-indicator">Chargement des utilisateurs...</div>;
  }

  if (error) {
    return <div className="error-message">Erreur: {error}</div>;
  }

  return (
    <div className="gestion-comptes">
      <div className="gestion-comptes-header">
        {/* Pour les prestataires : afficher les filtres */}
        {(isPrestataire || isPrestataireLibre) && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', width: '100%', marginBottom: '1rem' }}>
            <div style={{ flex: '1', minWidth: '200px' }}>
              <label htmlFor="lot-technique-filter" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Lot Technique
              </label>
              <select
                id="lot-technique-filter"
                value={lotTechniqueFilter}
                onChange={(e) => setLotTechniqueFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="">Tous les lots techniques</option>
                {availableLotsTechniques.map((lot: any) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.id} - {lot.nom}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label htmlFor="lot-departement-filter" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                Lot Département
              </label>
              <select
                id="lot-departement-filter"
                value={lotDepartementFilter}
                onChange={(e) => setLotDepartementFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="">Tous les départements</option>
                {availableLotsDepartements.map((lot: any) => (
                  <option key={lot.id} value={lot.id}>
                    {String(lot.id).padStart(2, '0')} - {lot.nom}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ flex: '1', minWidth: '200px' }}>
              <label htmlFor="contrat-filter" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                N° Contrat
              </label>
              <select
                id="contrat-filter"
                value={contratFilter}
                onChange={(e) => setContratFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}
              >
                <option value="">Tous les contrats</option>
                {availableContrats.map((contrat: string) => (
                  <option key={contrat} value={contrat}>
                    {contrat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Pour les acheteurs : afficher la barre de recherche et le bouton de création si admin */}
        {!isPrestataire && !isPrestataireLibre && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '1rem', gap: '1rem' }}>
            <div style={{ flex: '1' }}>
              <SearchBar
                placeholder="Rechercher un membre"
                onSearch={handleSearch}
              />
            </div>
            {isAdminRole(userRole) && (
              <Button
                value="Créer un utilisateur"
                variant="primary"
                size="small"
                icon={<PlusIcon />}
                onClick={() => setIsCreateModalOpen(true)}
                disabled={false}
              />
            )}
          </div>
        )}
      </div>
      <div className="tableau-section">
        <TableauDeBord
          data={filteredData}
          columns={columns}
          onSearch={handleSearch}
          onExport={handleExport}
          isSelectable={false}
          selectedItems={[]}
          onSelectionChange={() => { }}
          showActionsColumn={false}
          itemsPerPage={500}
        />
      </div>

      {/* Modals */}
      {isAdminRole(userRole) && (
        <>
          {/* Modal de création de compte */}
          <CreationComptesModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onCreate={handleCreateCompte}
          />

          {/* Modal de réinitialisation de mot de passe */}
          {isResetPasswordModalOpen && selectedUser && (
            <div className="modal-overlay" style={{ display: 'flex' }}>
              <div className="modal-container" style={{ maxWidth: '500px', width: '90%' }}>
                <div className="modal-header">
                  <h2>Réinitialiser le mot de passe</h2>
                  <button onClick={() => setIsResetPasswordModalOpen(false)} className="close-button">
                    <CrossIcon />
                  </button>
                </div>
                <div className="modal-body">
                  <p>
                    Voulez-vous envoyer un email de réinitialisation de mot de passe à <strong>{selectedUser.email}</strong> ?
                  </p>
                </div>
                <div className="modal-actions">
                  <Button
                    value="Annuler"
                    variant="secondary"
                    size="small"
                    onClick={() => setIsResetPasswordModalOpen(false)}
                  />
                  <Button
                    value="Envoyer"
                    variant="primary"
                    size="small"
                    onClick={handleConfirmResetPassword}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Modal d'édition utilisateur */}
          {selectedUser && (
            <EditUserModal
              isOpen={isEditUserModalOpen}
              onClose={() => setIsEditUserModalOpen(false)}
              onSave={handleSaveUser}
              userId={selectedUser.id}
              userName={selectedUser.nom}
              userEmail={selectedUser.email}
              userPrenom={selectedUser.prenom}
              userFonction={selectedUser.fonction}
              userRole={selectedUser.role}
            />
          )}

          {/* Modal de changement de rôle */}
          {selectedUser && (
            <div className={`modal-overlay ${isChangeRoleModalOpen ? 'show' : 'hide'}`}
              style={{ display: isChangeRoleModalOpen ? 'flex' : 'none' }}>
              <div className="change-role-modal">
                <h2>Changer le rôle de {selectedUser.nom}</h2>
                <p>Rôle actuel: {selectedUser.role}</p>

                {/* État temporaire pour stocker le nouveau rôle sélectionné */}
                <div className="role-options">
                  {/* Afficher les options de rôle appropriées en fonction du type d'utilisateur */}
                  {isPrestataireRole(selectedUser.role) ? (
                    // Options pour les prestataires
                    <>
                      <button
                        className={`role-button ${selectedUser.role === 'Prestataire'
                          ? 'active'
                          : tempSelectedRole === 'Prestataire'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Prestataire')}
                        disabled={selectedUser.role === 'Prestataire'}
                      >
                        Prestataire
                      </button>
                      <button
                        className={`role-button ${selectedUser.role === 'Prestataire signataire'
                          ? 'active'
                          : tempSelectedRole === 'Prestataire signataire'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Prestataire signataire')}
                        disabled={selectedUser.role === 'Prestataire signataire'}
                      >
                        Prestataire signataire
                      </button>
                    </>
                  ) : (
                    // Options pour les acheteurs
                    <>
                      <button
                        className={`role-button ${selectedUser.role === 'Acheteur'
                          ? 'active'
                          : tempSelectedRole === 'Acheteur'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Acheteur')}
                        disabled={selectedUser.role === 'Acheteur'}
                      >
                        Acheteur
                      </button>
                      <button
                        className={`role-button ${selectedUser.role === 'Acheteur signataire'
                          ? 'active'
                          : tempSelectedRole === 'Acheteur signataire'
                            ? 'selected'
                            : ''
                          }`}
                        onClick={() => setTempSelectedRole('Acheteur signataire')}
                        disabled={selectedUser.role === 'Acheteur signataire'}
                      >
                        Acheteur signataire
                      </button>
                      <button
                        className="role-button disabled"
                        disabled={true}
                        title="🚧 Fonctionnalité en développement"
                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                      >
                        Observateur 🚧
                      </button>
                    </>
                  )}
                </div>

                <div className="modal-actions">
                  <Button
                    variant="secondary"
                    size="small"
                    value="Annuler"
                    onClick={() => {
                      setIsChangeRoleModalOpen(false);
                      setTempSelectedRole(null);
                    }}
                  />
                  <Button
                    variant="primary"
                    size="small"
                    value="Valider"
                    disabled={!tempSelectedRole || tempSelectedRole === selectedUser.role}
                    onClick={() => {
                      if (tempSelectedRole) {
                        handleChangeRole(tempSelectedRole);
                        setTempSelectedRole(null);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal de réattribution d'interlocuteur (Prestataires AC et Libres) */}
      {(isPrestataire || isPrestataireLibre) && selectedLotContrat && (
        <div className={`modal-overlay ${isReassignModalOpen ? 'show' : 'hide'}`}
          style={{ display: isReassignModalOpen ? 'flex' : 'none' }}>
          <div className="change-role-modal">
            <div className="modal-header">
              <h2>Réattribuer un interlocuteur</h2>
              <button
                onClick={() => setIsReassignModalOpen(false)}
                className="close-button"
              >
                <CrossIcon />
              </button>
            </div>

            <div className="modal-body">
              <div className="info-section">
                <div className="info-row">
                  <span className="info-label">Lot Technique:</span>
                  <span className="info-value">{selectedLotContrat.lotTechnique?.nom}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Département:</span>
                  <span className="info-value">{selectedLotContrat.lotDepartement?.nom}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">N° Contrat:</span>
                  <span className="info-value">{selectedLotContrat.contratNumero}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Interlocuteur actuel:</span>
                  <span className="info-value">{selectedLotContrat.nom}</span>
                </div>
              </div>

              <div className="form-group-modal">
                <label htmlFor="new-interlocuteur">
                  Nouvel interlocuteur :
                </label>
                <select
                  id="new-interlocuteur"
                  className="form-select"
                  value={newInterlocuteurId}
                  onChange={(e) => setNewInterlocuteurId(e.target.value)}
                >
                  <option value="">-- Sélectionner un interlocuteur --</option>
                  {availableInterlocuteurs.map((interlocuteur: any) => (
                    <option key={interlocuteur.id} value={interlocuteur.id}>
                      {interlocuteur.prenom} {interlocuteur.nom} - {interlocuteur.type} ({interlocuteur.email_contact})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-actions">
              <Button
                variant="secondary"
                size="small"
                value="Annuler"
                onClick={() => {
                  setIsReassignModalOpen(false);
                  setNewInterlocuteurId('');
                }}
              />
              <Button
                variant="primary"
                size="small"
                value={isReassigning ? "En cours..." : "Valider"}
                disabled={!newInterlocuteurId || isReassigning}
                onClick={handleReassignInterlocuteur}
              />
            </div>
          </div>
        </div>
      )}

      {/* FRONT-16: Modal d'attribution de MEMBRE à un LOT (Prestataires Libres) */}
      {isPrestataireLibre && selectedLotForMember && (
        <div className={`modal-overlay ${isLotAssignModalOpen ? 'show' : 'hide'}`}
          style={{ display: isLotAssignModalOpen ? 'flex' : 'none' }}>
          <div className="change-role-modal lot-assign-modal">
            <div className="modal-header">
              <h2>Attribuer un membre au lot</h2>
              <button
                onClick={() => setIsLotAssignModalOpen(false)}
                className="close-button"
              >
                <CrossIcon />
              </button>
            </div>

            <div className="modal-body">
              {/* Informations du lot en lecture seule */}
              <div className="info-section" style={{ marginBottom: '1.5rem' }}>
                <div className="info-row">
                  <span className="info-label">Lot Technique :</span>
                  <span className="info-value" style={{ fontWeight: 'bold' }}>
                    {selectedLotForMember.lotTechnique?.id} - {selectedLotForMember.lotTechnique?.nom}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Département :</span>
                  <span className="info-value" style={{ fontWeight: 'bold' }}>
                    {String(selectedLotForMember.lotDepartement?.id || '').padStart(2, '0')} - {selectedLotForMember.lotDepartement?.nom}
                  </span>
                </div>
                {selectedLotForMember.assignedMemberId && (
                  <div className="info-row" style={{ marginTop: '0.5rem', color: '#666' }}>
                    <span className="info-label">Actuellement attribué à :</span>
                    <span className="info-value">{selectedLotForMember.nom}</span>
                  </div>
                )}
              </div>

              {/* Dropdown pour sélectionner le membre */}
              <div className="form-group-modal">
                <label htmlFor="member-select">
                  Membre à assigner <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  id="member-select"
                  className="form-select"
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                >
                  <option value="">-- Sélectionner un membre --</option>
                  {availableMembersForLot.map((membre: any) => (
                    <option key={membre.id} value={membre.id}>
                      {membre.nom} ({membre.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="modal-actions">
              {/* Bouton pour retirer l'attribution si le lot est déjà attribué */}
              {selectedLotForMember.lotAssignmentId && (
                <Button
                  variant="secondary"
                  size="small"
                  value="Retirer"
                  onClick={handleRemoveLotAssignment}
                  disabled={isAssigningLot}
                />
              )}
              <Button
                variant="secondary"
                size="small"
                value="Annuler"
                onClick={() => {
                  setIsLotAssignModalOpen(false);
                  setSelectedMemberId('');
                }}
              />
              <Button
                variant="primary"
                size="small"
                value={isAssigningLot ? "En cours..." : "Enregistrer"}
                disabled={!selectedMemberId || isAssigningLot}
                onClick={handleSaveLotAssignment}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de passation de gestionnaire */}
      <PassationModal
        isOpen={isPassationModalOpen}
        onClose={() => {
          setIsPassationModalOpen(false);
          setPendingPromotion(null);
          setGestionnairesActifs([]);
        }}
        onConfirm={handleConfirmPassation}
        gestionnairesActifs={gestionnairesActifs}
        nouveauGestionnaire={pendingPromotion?.user || null}
      />
    </div>
  );
};

export default GestionComptes;